import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { EditMode, MapData3dObject } from '@repo/types/maps/map-3d-types';
import { mapsApi } from '../api/mapsApi';
import type { RootState } from '../store';
import { selectExternalMapId, updateMapObject3dData } from './mapData';

export interface MapObjects3dState {
    selectedObjectId: string | null;
    editMode: EditMode | null;
    isAdding: boolean;
    isMoving: boolean;
    movingObjectId: string | null;
    draftObject: MapData3dObject | null;
}

const initialState: MapObjects3dState = {
    selectedObjectId: null,
    editMode: null,
    isAdding: false,
    isMoving: false,
    movingObjectId: null,
    draftObject: null,
};

export const createMapObject3d = createAsyncThunk(
    'mapObjects3d/create',
    async (
        {
            lng,
            lat,
            properties,
        }: {
            lng: number;
            lat: number;
            properties?: Partial<MapData3dObject['properties']>;
        },
        { getState, dispatch },
    ) => {
        const externalId = selectExternalMapId(getState() as RootState);

        if (!externalId) {
            throw new Error('No map selected');
        }

        const result = await dispatch(
            mapsApi.endpoints.createMap3dObject.initiate({
                gameMapExternalId: externalId,
                geometry: {
                    type: 'Point',
                    coordinates: [lng, lat],
                },
                properties: {
                    title: properties?.title ?? 'New 3D Object',
                    type: properties?.type,
                    scale: properties?.scale ?? { x: 1, y: 1, z: 1 },
                    quaternion: properties?.quaternion ?? { x: 0, y: 0, z: 0, w: 1 },
                    minZoom: properties?.minZoom ?? 1,
                    maxZoom: properties?.maxZoom ?? 20,
                    modelPath: properties?.modelPath,
                    color: properties?.color,
                    intensity: properties?.intensity,
                },
            }),
        ).unwrap();

        return result;
    },
);

export const updateMapObject3d = createAsyncThunk(
    'mapObjects3d/update',
    async (
        {
            objectId,
            properties,
            geometry,
        }: {
            objectId: string;
            properties: Partial<MapData3dObject['properties']>;
            geometry?: { type: 'Point'; coordinates: [number, number] | [number, number, number] };
        },
        { getState, dispatch },
    ) => {
        const externalId = selectExternalMapId(getState() as RootState);

        if (!externalId) {
            throw new Error('No map selected');
        }

        const completeProperties = {
            title: properties.title ?? 'Unnamed 3D Object',
            type: properties.type ?? 'model',
            scale: properties.scale ?? { x: 1, y: 1, z: 1 },
            quaternion: properties.quaternion ?? { x: 0, y: 0, z: 0, w: 1 },
            minZoom: properties.minZoom ?? 1,
            maxZoom: properties.maxZoom ?? 20,
            ...(properties.modelPath !== undefined && { modelPath: properties.modelPath }),
            ...(properties.color !== undefined && { color: properties.color }),
            ...(properties.intensity !== undefined && { intensity: properties.intensity }),
        };

        const result = await dispatch(
            mapsApi.endpoints.updateMap3dObject.initiate({
                externalId: objectId,
                request: {
                    properties: completeProperties,
                    geometry: geometry || { type: 'Point', coordinates: [0, 0] },
                },
                gameMapExternalId: externalId,
            }),
        ).unwrap();

        dispatch(updateMapObject3dData({ externalId: objectId, updatedObject: result }));

        return result;
    },
);

export const deleteMapObject3d = createAsyncThunk(
    'mapObjects3d/delete',
    async ({ objectId }: { objectId: string }, { getState, dispatch }) => {
        const externalId = selectExternalMapId(getState() as RootState);

        if (!externalId) {
            throw new Error('No map selected');
        }

        await dispatch(
            mapsApi.endpoints.deleteMap3dObject.initiate({
                externalId: objectId,
                gameMapExternalId: externalId,
            }),
        ).unwrap();

        return objectId;
    },
);

export const moveMapObject3d = createAsyncThunk(
    'mapObjects3d/move',
    async (
        {
            object,
            lng,
            lat,
        }: {
            object: MapData3dObject;
            lng: number;
            lat: number;
        },
        { getState, dispatch },
    ) => {
        const externalId = selectExternalMapId(getState() as RootState);

        if (!externalId) {
            throw new Error('No map selected');
        }

        const result = await dispatch(
            mapsApi.endpoints.updateMap3dObject.initiate({
                externalId: object.properties.externalId,
                request: {
                    geometry: {
                        type: 'Point',
                        coordinates: [lng, lat],
                    },
                    properties: object.properties,
                },
                gameMapExternalId: externalId,
            }),
        ).unwrap();

        return result;
    },
);

const mapObjects3dSlice = createSlice({
    name: 'mapObjects3d',
    initialState,
    reducers: {
        setSelectedMapObject3dId: (state, action: PayloadAction<string | null>) => {
            state.selectedObjectId = action.payload;
        },
        setMapObject3dEditMode: (state, action: PayloadAction<EditMode | null>) => {
            state.editMode = action.payload;
        },
        setIsAddingMapObject3d: (state, action: PayloadAction<boolean>) => {
            state.isAdding = action.payload;
        },
        setIsMovingMapObject3d: (state, action: PayloadAction<{ isMoving: boolean; objectId: string | null }>) => {
            state.isMoving = action.payload.isMoving;
            state.movingObjectId = action.payload.objectId;
        },
        cancelAddingMapObject3d: state => {
            state.isAdding = false;
        },
        cancelMovingMapObject3d: state => {
            state.isMoving = false;
            state.movingObjectId = null;
        },
        setDraftObject3d: (state, action: PayloadAction<MapData3dObject | null>) => {
            state.draftObject = action.payload;
        },
        updateDraftObject3d: (
            state,
            action: PayloadAction<{
                properties?: Partial<MapData3dObject['properties']>;
                geometry?: Partial<MapData3dObject['geometry']>;
            }>,
        ) => {
            if (!state.draftObject) return;

            if (action.payload.properties) {
                state.draftObject = {
                    ...state.draftObject,
                    properties: {
                        ...state.draftObject.properties,
                        ...action.payload.properties,
                    },
                };
            }

            if (action.payload.geometry) {
                state.draftObject = {
                    ...state.draftObject,
                    geometry: {
                        ...state.draftObject.geometry,
                        ...action.payload.geometry,
                    },
                };
            }
        },
        clearDraftObject3d: state => {
            state.draftObject = null;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(createMapObject3d.fulfilled, state => {
                state.isAdding = false;
            })
            .addCase(deleteMapObject3d.fulfilled, state => {
                state.selectedObjectId = null;
                state.editMode = null;
                state.draftObject = null;
            })
            .addCase(moveMapObject3d.fulfilled, state => {
                state.isMoving = false;
                state.movingObjectId = null;
            })
            .addCase(updateMapObject3d.fulfilled, state => {
                state.draftObject = null;
            });
    },
});

export const {
    setSelectedMapObject3dId,
    setMapObject3dEditMode,
    setIsAddingMapObject3d,
    setIsMovingMapObject3d,
    cancelAddingMapObject3d,
    cancelMovingMapObject3d,
    setDraftObject3d,
    updateDraftObject3d,
    clearDraftObject3d,
} = mapObjects3dSlice.actions;

export default mapObjects3dSlice.reducer;

export const selectSelectedMapObject3dId = (state: { mapObjects3d: MapObjects3dState }) =>
    state.mapObjects3d.selectedObjectId;
export const selectMapObject3dEditMode = (state: { mapObjects3d: MapObjects3dState }) => state.mapObjects3d.editMode;
export const selectIsAddingMapObject3d = (state: { mapObjects3d: MapObjects3dState }) => state.mapObjects3d.isAdding;
export const selectIsMovingMapObject3d = (state: { mapObjects3d: MapObjects3dState }) => state.mapObjects3d.isMoving;
export const selectMovingMapObject3dId = (state: { mapObjects3d: MapObjects3dState }) =>
    state.mapObjects3d.movingObjectId;
export const selectDraftObject3d = (state: { mapObjects3d: MapObjects3dState }) => state.mapObjects3d.draftObject;
